// FILE: client/src/pages/secure/AdvertisingReviewPage.js
// (This file is updated to fix the API endpoint URLs)

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { format } from 'date-fns';
import DataList from '../../components/shared/DataList';

const statusStyles = {
    'Pending Review': 'bg-yellow-100 text-yellow-800',
    'Approved': 'bg-green-100 text-green-800',
    'Rejected': 'bg-red-100 text-red-800',
    'Changes Requested': 'bg-blue-100 text-blue-800',
};

// --- Submit Ad for Review Modal Component ---
const SubmitAdModal = ({ isOpen, onClose, onAdSubmitted }) => {
    const [properties, setProperties] = useState([]);
    const [adFile, setAdFile] = useState(null);
    const [formData, setFormData] = useState({
        ad_title: '',
        transaction_id: '',
    });
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const apiClient = useApiClient();

    useEffect(() => {
        if (isOpen) {
            apiClient.get('/api/transactions')
                .then(data => {
                    if (Array.isArray(data)) setProperties(data);
                })
                .catch(() => setError('Could not load transactions list.'));
            
            setFormData({ ad_title: '', transaction_id: '' });
            setAdFile(null);
            setError('');
        }
    }, [isOpen, apiClient]);

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleFileChange = (e) => {
        setAdFile(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!adFile) {
            setError('Please select a file to submit.');
            return;
        }
        setError('');
        setIsSubmitting(true);

        const submissionData = new FormData();
        submissionData.append('adFile', adFile);
        submissionData.append('ad_title', formData.ad_title);
        if (formData.transaction_id) {
            submissionData.append('transaction_id', formData.transaction_id);
        }

        try {
            // **FIX**: The useApiClient handles FormData and auth correctly.
            const result = await apiClient.post('/api/advertising', submissionData);
            onAdSubmitted(result);
            onClose();
        } catch (err) {
            setError(err.message || 'Submission failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-lg">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Submit Ad for Review</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input name="ad_title" value={formData.ad_title} onChange={handleChange} placeholder="Ad Title (e.g., 'Flyer for 123 Main St')" required className="p-2 border rounded w-full" />
                    <select name="transaction_id" value={formData.transaction_id} onChange={handleChange} className="w-full p-2 border rounded">
                        <option value="">No Specific Transaction</option>
                        {properties.map(prop => (
                            <option key={prop.transaction_id} value={prop.transaction_id}>{prop.street_address}</option>
                        ))}
                    </select>
                    <input type="file" onChange={handleFileChange} required className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"/>
                    
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// --- Main Advertising Review Page Component ---
const AdvertisingReviewPage = () => {
    const [ads, setAds] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const apiClient = useApiClient();

    const fetchAds = useCallback(() => {
        setIsLoading(true);
        // **FIX**: Changed endpoint from '/api/advertising/materials' to '/api/advertising'
        apiClient.get('/api/advertising')
            .then(data => {
                if(Array.isArray(data)) setAds(data);
            })
            .catch(err => setError(err.message || 'Failed to fetch advertisements.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    useEffect(() => {
        fetchAds();
    }, [fetchAds]);

    const handleAdSubmitted = () => {
        fetchAds(); // Re-fetch the list after a new ad is submitted
    };

    return (
        <>
            <SubmitAdModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onAdSubmitted={handleAdSubmitted}
            />
            <div className="bg-white p-8 rounded-lg shadow">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-slate-800">Advertising Review</h1>
                    <button 
                        onClick={() => setIsModalOpen(true)}
                        className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900"
                    >
                        Submit New Ad for Review
                    </button>
                </div>

                {isLoading && <p>Loading advertisements...</p>}
                {error && <p className="text-red-500">{error}</p>}
                {!isLoading && !error && (
                    <div className="overflow-x-auto">
                        <div className="p-4"><DataList rows={ads} rowKey="ad_id" csvName="advertising-reviews" defaultSort={{ key: 'submission_date', dir: 'desc' }} empty="No ads submitted."
                            filters={[{ key: 'status', label: 'Status' }, { key: 'agent_name', label: 'Submitted by' }]}
                            columns={[
                                { key: 'ad_title', label: 'Ad Title', render: (ad) => <span className="font-medium text-slate-900">{ad.ad_title}</span> },
                                { key: 'agent_name', label: 'Submitted By' },
                                { key: 'submission_date', label: 'Date Submitted', type: 'date', render: (ad) => format(new Date(ad.submission_date), 'P') },
                                { key: 'status', label: 'Status', render: (ad) => <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusStyles[ad.status]}`}>{ad.status}</span> },
                                { key: 'actions', label: 'Actions', sortable: false, csv: false, search: false, render: (ad) => <Link to={`/dashboard/advertising/${ad.ad_id}`} className="text-indigo-600 hover:text-indigo-900">Review</Link> },
                            ]} /></div>
                    </div>
                )}
            </div>
        </>
    );
};

export default AdvertisingReviewPage;
